import React, { useState } from 'react';
import { Button } from '@asigna/kit';
import { archiveMultisig } from 'api';
import { ModalCloseButton, NotificationCard } from 'components';
import useAddress from 'hooks/use-address';
import { useSetAtom } from 'jotai';
import { toast } from 'sonner';
import { updatedArchievedSafeAtom, useCurrentSafe } from 'store';
import { IOwner } from 'types';

import boxIcon from './utils/images/box.svg';

interface TransactionCommonProps {
  id: string;
  onClose: () => void;
}

export const ArchiveVaultDialog: React.FC<TransactionCommonProps> = ({ id, onClose }) => {
  const { currentSafe } = useCurrentSafe();
  const updateSafes = useSetAtom(updatedArchievedSafeAtom);
  const walletAccount = useAddress();
  const [isLoading, setLoading] = useState(false);

  const isArchiveVault = ((currentSafe?.owners as IOwner[]) || []).find(
    (owner) => owner.address === walletAccount
    // TODO remove ts-ignore, add proper fix
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
  )?.archived;

  const handleArchive = async () => {
    setLoading(true);
    try {
      await archiveMultisig(id, isArchiveVault ? 'unset' : 'set').then((response) => {
        if (response.success) {
          updateSafes(response.data, !isArchiveVault);
          toast(
            () => (
              <NotificationCard
                title="Success!"
                message={
                  isArchiveVault ? 'Vault unarchived successfully.' : 'Vault archived successfully.'
                }
                type="success"
              />
            ),
            {
              className: 'w-full',
            }
          );
        }
        onClose();
      });
    } catch (error: unknown) {
      console.error('Unknown error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-32">
      <div className="flex items-center justify-between">
        <p className="text-r20">{isArchiveVault ? 'Unarchive Vault' : 'Archive Vault'}</p>
        <ModalCloseButton onClick={onClose} />
      </div>

      <div className="flex flex-col items-center gap-16">
        <div className="flex w-56 h-56 items-center justify-center border border-white-10 rounded-[56px] mb-8">
          <img src={boxIcon} width={24} height={24} alt="delete icon" />
        </div>
        {isArchiveVault ? (
          <p className="text-r20 text-white text-center">Do you want to unarchive this vault?</p>
        ) : (
          <p className="text-r20 text-white text-center">Do you want to archive this vault?</p>
        )}
        {!isArchiveVault && (
          <span className="text-m14 text-regular text-center">
            You can unarchive your vaults at any time.
          </span>
        )}
      </div>
      <div className="flex items-center gap-16 w-full">
        <Button message="Cancel" onClick={onClose} colorType="secondary" fullWidth />
        <Button
          message={isArchiveVault ? 'Unarchive' : 'Yes, Archive'}
          onClick={handleArchive}
          colorType="primary"
          fullWidth
          leftIcon={isLoading ? '/loading-gif.gif' : undefined}
          disabled={isLoading}
        />
      </div>
    </div>
  );
};
